import type { IDisposable } from '@repo/types/shared/disposable';
import { disposify } from '@repo/types/shared/util';
import { DisposableEventAggregator } from './disposable-event-aggregator';

// JSON-serializable types, plus Error and undefined.
// Cannot use keyed record for stronger typing due to TypeScript issue:
// https://github.com/microsoft/TypeScript/issues/15300
export type MessageData = string | number | boolean | null | MessageData[] | object | Error | undefined;

export class UnhandledMessageError extends Error {
    public constructor(message: string) {
        super(message);
        Object.setPrototypeOf(this, UnhandledMessageError.prototype);
    }
}

export interface RpcChannel extends IDisposable {
    execute(action: string, data: MessageData): Promise<MessageData>;
    setHandler<T extends Readonly<MessageData>>(
        action: string,
        handler: (data: T) => Promise<MessageData> | MessageData,
    ): void;
    removeHandler(action: string): void;
}

export class MockRpcChannel implements RpcChannel {
    public execute(): Promise<MessageData> {
        console.warn('MockRpcChannel.execute called, but no implementation provided.');
        return new Promise(() => {});
    }

    public setHandler(): void {}

    public removeHandler(): void {}

    public dispose(): void {}
}

export interface LocalRpcConfig {
    setExecutor(executor: (action: string, data: MessageData) => Promise<MessageData>): void;
    readonly handlers: Record<string, (data: MessageData) => Promise<MessageData>>;
}

export class LocalRpcChannel implements RpcChannel {
    readonly #remoteHandlers: Map<string, (data: MessageData) => Promise<MessageData>>;
    readonly #handlers = new Map<string, (data: MessageData) => Promise<MessageData> | MessageData>();

    public constructor(config: LocalRpcConfig) {
        this.#remoteHandlers = new Map<string, (data: MessageData) => Promise<MessageData>>(
            Object.entries(config.handlers),
        );
        config.setExecutor(async (action, data) => {
            const handler = this.#handlers.get(action);

            if (handler) {
                return await handler(data);
            } else {
                throw new UnhandledMessageError(`No handler defined for message type ${action}.`);
            }
        });
    }

    public async execute(action: string, data: MessageData): Promise<MessageData> {
        const handler = this.#remoteHandlers.get(action);

        if (handler) {
            return await handler(data);
        } else {
            throw new UnhandledMessageError(`No handler defined for message type ${action}.`);
        }
    }

    public setHandler<T extends Readonly<MessageData>>(
        action: string,
        handler: (data: T) => Promise<MessageData> | MessageData,
    ): void {
        this.#handlers.set(action, handler as (data: MessageData) => Promise<MessageData> | MessageData);
    }

    public removeHandler(action: string): void {
        this.#handlers.delete(action);
    }

    public dispose(): void {
        this.#handlers.clear();
    }
}

interface Message {
    readonly id: number;
    readonly type: string;
    readonly data: MessageData;
}

interface ResolveMessage extends Message {
    readonly type: '_resolve';
}

interface RejectMessage extends Message {
    readonly type: '_reject';
    readonly data: Error;
}

interface UnhandledMessage extends Message {
    readonly type: '_unhandled';
    readonly data: {
        readonly type: string;
    };
}

export interface IMessagePort {
    onMessage(handler: (event: MessageEvent) => void): IDisposable;
    postMessage(message: Message): void;
    close(): void;
}

export class WindowMessagePort implements IMessagePort {
    #targetWindow: Window | null;
    #messageListener: IDisposable | null;

    readonly #targetOrigin: string;
    readonly #events = new DisposableEventAggregator();

    public constructor(targetWindow: Window, targetOrigin: string) {
        this.#targetWindow = targetWindow;
        this.#targetOrigin = targetOrigin;

        const messageHandler = (event: MessageEvent): void => {
            if (event.source === this.#targetWindow) {
                this.#events.publish('_', event);
            }
        };

        window.addEventListener('message', messageHandler);

        this.#messageListener = disposify(() => window.removeEventListener('message', messageHandler));
    }

    public onMessage(handler: (event: MessageEvent) => void): IDisposable {
        return this.#events.subscribe('_', handler);
    }

    public postMessage(message: Message, transfer?: Transferable[]): void {
        this.#targetWindow?.postMessage(message, this.#targetOrigin, transfer);
    }

    public close(): void {
        this.#messageListener?.dispose();
        this.#messageListener = null;
        this.#events?.dispose();
        this.#targetWindow = null;
    }
}

export class NativeMessagePort implements IMessagePort {
    #port: MessagePort | null;

    public constructor(port: MessagePort) {
        this.#port = port;

        port.start();
    }

    public onMessage(handler: (event: MessageEvent) => void): IDisposable {
        if (!this.#port) {
            throw new Error('RPC channel was disposed.');
        }

        this.#port.addEventListener('message', handler);

        return disposify(() => this.#port?.removeEventListener('message', handler));
    }

    public postMessage(message: Message): void {
        console.debug('Posting message through NativeMessagePort:', message);
        this.#port?.postMessage(message);
    }

    public close(): void {
        this.#port?.close();
        this.#port = null;
    }
}

export class MessagePortRpcChannel implements RpcChannel {
    #nextId: number = 1;
    #port: IMessagePort | null;
    #messageListener: IDisposable | null;

    readonly #handlers = new Map<string, (data: MessageData) => Promise<MessageData> | MessageData>();
    readonly #pending = new Map<number, [(result: MessageData) => void, (error: MessageData) => void]>();

    public constructor(port: IMessagePort) {
        this.#port = port;

        this.#messageListener = port.onMessage(event => {
            const data = event.data;

            if (typeof data === 'object' && data !== null) {
                if (typeof data.id === 'number' && typeof data.type === 'string') {
                    void this.#handleMessage(data);
                }
            }
        });
    }

    public execute(action: string, data?: MessageData): Promise<MessageData> {
        if (!this.#port) {
            console.error('RPC channel is disposed, cannot execute action:', action);
            throw new Error('RPC channel was disposed.');
        }

        const id = this.#nextId++;
        const promise = new Promise<MessageData>((resolve, reject) => {
            this.#pending.set(id, [resolve, reject]);
        });

        this.#postMessage({ id, type: action, data });

        return promise;
    }

    public setHandler<T extends Readonly<MessageData>>(
        action: string,
        handler: (data: T) => Promise<MessageData> | MessageData,
    ): void {
        if (!this.#port) {
            throw new Error('RPC channel was disposed.');
        }

        this.#handlers.set(action, handler as (data: MessageData) => Promise<MessageData> | MessageData);
    }

    public removeHandler(action: string): void {
        this.#handlers.delete(action);
    }

    async #handleMessage(message: Message): Promise<void> {
        if (message.type === '_resolve' || message.type === '_reject' || message.type === '_unhandled') {
            const settle = this.#pending.get(message.id);

            if (settle) {
                this.#pending.delete(message.id);

                if (message.type === '_resolve') {
                    const resolveMessage = message as ResolveMessage;

                    settle[0](resolveMessage.data);
                } else if (message.type === '_reject') {
                    const rejectMessage = message as RejectMessage;

                    settle[1](rejectMessage.data);
                } else if (message.type === '_unhandled') {
                    const unhandledMessage = message as UnhandledMessage;

                    settle[1](
                        new UnhandledMessageError(`No handler defined for message type ${unhandledMessage.data.type}.`),
                    );
                }
            }
        } else {
            const handler = this.#handlers.get(message.type);

            if (handler) {
                let resultType: '_resolve' | '_reject';
                let resultData: MessageData;

                try {
                    resultData = await handler(message.data);
                    resultType = '_resolve';
                } catch (error) {
                    resultData = error instanceof Error ? error : new Error(String(error));
                    resultType = '_reject';
                }

                this.#postMessage({
                    id: message.id,
                    type: resultType,
                    data: resultData,
                });
            } else {
                this.#postMessage({
                    id: message.id,
                    type: '_unhandled',
                    data: { type: message.type },
                });
            }
        }
    }

    #postMessage(message: Message): void {
        this.#port?.postMessage(message);
    }

    public dispose(): void {
        this.#messageListener?.dispose();
        this.#messageListener = null;
        this.#handlers.clear();
        this.#port?.close();
        this.#port = null;

        for (const [, reject] of this.#pending.values()) {
            reject(new Error('RPC channel was disposed.'));
        }

        this.#pending.clear();
    }
}

export class WindowRpcChannel extends MessagePortRpcChannel {
    public constructor(targetWindow: Window, targetOrigin: string) {
        super(new WindowMessagePort(targetWindow, targetOrigin));
    }
}

export class WebviewContentMessagePort implements IMessagePort {
    readonly #events = new DisposableEventAggregator();
    #messageListener: IDisposable | null;

    public constructor() {
        const messageHandler = (event: MessageEvent): void => {
            if (event.source === window || event.source === null) {
                const data = event.data;

                if (
                    (event.source === window || event.source === null) &&
                    event.origin === window.location.origin &&
                    typeof data === 'object' &&
                    data !== null &&
                    typeof data.id === 'number' &&
                    typeof data.type === 'string' &&
                    data.__source === 'host'
                ) {
                    const { __source, ...cleanData } = data;

                    const cleanEvent = new MessageEvent('message', {
                        data: cleanData,
                        origin: event.origin,
                        source: event.source,
                    });

                    this.#events.publish('_', cleanEvent);
                }
            }
        };

        window.addEventListener('message', messageHandler);
        this.#messageListener = disposify(() => window.removeEventListener('message', messageHandler));
    }

    public onMessage(handler: (event: MessageEvent) => void): IDisposable {
        return this.#events.subscribe('_', handler);
    }

    public postMessage(message: Message): void {
        window.postMessage(message, window.location.origin);
    }

    public close(): void {
        this.#messageListener?.dispose();
        this.#messageListener = null;
        this.#events?.dispose();
    }
}

export class WebviewContentRpcChannel extends MessagePortRpcChannel {
    public constructor() {
        super(new WebviewContentMessagePort());
    }
}
